import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import DisputeForm from "./components/DisputeForm";
import AdminDashboard from "./components/AdminDashboard";
import UsersAdmin from "./components/UsersAdmin";
import SettingsAdmin from "./components/SettingsAdmin";
import RainWizard from "./components/rain/RainWizard";
import HistoryPage from "./components/rain/HistoryPage";
import Home from "./components/Home";
import Login from "./components/Login";
import Signup from "./components/Signup";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";

interface NavigationProps {
  onNavigate: (path: string) => void;
}

const ROLE_LABEL: Record<string, string> = {
  user: "User",
  admin: "Admin",
  super_admin: "Super Admin",
};

const Navigation: React.FC<NavigationProps> = ({ onNavigate }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user, logout } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isAdminArea = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (!isAdminMenuOpen && !isUserMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (!adminMenuRef.current?.contains(e.target as Node)) {
        setIsAdminMenuOpen(false);
      }
      if (!userMenuRef.current?.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isAdminMenuOpen, isUserMenuOpen]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    path: string
  ) => {
    e.preventDefault();
    onNavigate(path);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
    setIsAdminMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    onNavigate("/");
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="app-navigation">
      <div className="nav-container">
        <div
          className="nav-brand"
          onClick={() => onNavigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img src="/Logo.png" alt="AI Judge" className="nav-logo" />
        </div>

        {/* Desktop Navigation */}
        <div className="nav-links desktop-nav">
          <a
            href="/rain"
            onClick={(e) => handleNavClick(e, "/rain")}
            className={`nav-link ${
              location.pathname === "/rain" ? "active" : ""
            }`}
          >
            Request a Decision
          </a>
          {isAdmin && (
            <a
              href="/history"
              onClick={(e) => handleNavClick(e, "/history")}
              className={`nav-link ${
                location.pathname === "/history" ? "active" : ""
              }`}
            >
              History
            </a>
          )}
          {isAdmin && !isSuperAdmin && (
            <a
              href="/admin"
              onClick={(e) => handleNavClick(e, "/admin")}
              className={`nav-link ${
                location.pathname === "/admin" ? "active" : ""
              }`}
            >
              Admin
            </a>
          )}
          {isSuperAdmin && (
            <div className="nav-dropdown" ref={adminMenuRef}>
              <button
                className={`nav-link nav-dropdown-toggle ${
                  isAdminArea ? "active" : ""
                }`}
                onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                aria-expanded={isAdminMenuOpen}
              >
                Admin
                <svg
                  className={`nav-chevron ${isAdminMenuOpen ? "open" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M7 10l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isAdminMenuOpen && (
                <div className="nav-dropdown-menu">
                  <a
                    href="/admin"
                    onClick={(e) => handleNavClick(e, "/admin")}
                    className={`nav-dropdown-item ${
                      location.pathname === "/admin" ? "active" : ""
                    }`}
                  >
                    Dashboard
                  </a>
                  <a
                    href="/admin/users"
                    onClick={(e) => handleNavClick(e, "/admin/users")}
                    className={`nav-dropdown-item ${
                      location.pathname === "/admin/users" ? "active" : ""
                    }`}
                  >
                    Users
                  </a>
                  <a
                    href="/admin/settings"
                    onClick={(e) => handleNavClick(e, "/admin/settings")}
                    className={`nav-dropdown-item ${
                      location.pathname === "/admin/settings" ? "active" : ""
                    }`}
                  >
                    Settings
                  </a>
                </div>
              )}
            </div>
          )}
          <a
            href="/dispute"
            onClick={(e) => handleNavClick(e, "/dispute")}
            className={`nav-link nav-link-secondary ${
              location.pathname === "/dispute" ? "active" : ""
            }`}
          >
            Tomi (Legacy)
          </a>

          {isAuthenticated && user && (
            <div className="nav-dropdown nav-user-dropdown" ref={userMenuRef}>
              <button
                className="nav-identity"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-expanded={isUserMenuOpen}
              >
                <span className="nav-avatar">
                  {(user.firstName || user.email || "?")
                    .charAt(0)
                    .toUpperCase()}
                </span>
                <span className="nav-identity-name">
                  {user.firstName || user.email}
                </span>
                <svg
                  className={`nav-chevron ${isUserMenuOpen ? "open" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M7 10l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isUserMenuOpen && (
                <div className="nav-dropdown-menu nav-user-menu">
                  <div className="nav-user-meta">
                    <div className="nav-user-fullname">
                      {[user.firstName, user.lastName]
                        .filter(Boolean)
                        .join(" ") || user.email}
                    </div>
                    <div className="nav-user-email">{user.email}</div>
                    {user.role && (
                      <span className="nav-user-role">
                        {ROLE_LABEL[user.role] || user.role}
                      </span>
                    )}
                  </div>
                  <button
                    className="nav-dropdown-item nav-user-logout"
                    onClick={handleLogout}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span
            className={`hamburger-line ${isMobileMenuOpen ? "open" : ""}`}
          ></span>
          <span
            className={`hamburger-line ${isMobileMenuOpen ? "open" : ""}`}
          ></span>
          <span
            className={`hamburger-line ${isMobileMenuOpen ? "open" : ""}`}
          ></span>
        </button>

        {/* Mobile Navigation Dropdown */}
        <div className={`mobile-nav ${isMobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-nav-content">
            {isAuthenticated && user && (
              <div className="mobile-nav-user">
                {user.firstName || user.email}
                {user.role && (
                  <span className="mobile-nav-role">
                    {ROLE_LABEL[user.role] || user.role}
                  </span>
                )}
              </div>
            )}
            <a
              href="/rain"
              onClick={(e) => handleNavClick(e, "/rain")}
              className={`mobile-nav-link ${
                location.pathname === "/rain" ? "active" : ""
              }`}
            >
              Request a Decision
            </a>
            {isAdmin && (
              <a
                href="/history"
                onClick={(e) => handleNavClick(e, "/history")}
                className={`mobile-nav-link ${
                  location.pathname === "/history" ? "active" : ""
                }`}
              >
                History
              </a>
            )}
            {isAdmin && (
              <>
                {isSuperAdmin && (
                  <div className="mobile-nav-section">Administration</div>
                )}
                <a
                  href="/admin"
                  onClick={(e) => handleNavClick(e, "/admin")}
                  className={`mobile-nav-link ${
                    location.pathname === "/admin" ? "active" : ""
                  }`}
                >
                  {isSuperAdmin ? "Dashboard" : "Admin"}
                </a>
              </>
            )}
            {isSuperAdmin && (
              <a
                href="/admin/users"
                onClick={(e) => handleNavClick(e, "/admin/users")}
                className={`mobile-nav-link ${
                  location.pathname === "/admin/users" ? "active" : ""
                }`}
              >
                Users
              </a>
            )}
            {isSuperAdmin && (
              <a
                href="/admin/settings"
                onClick={(e) => handleNavClick(e, "/admin/settings")}
                className={`mobile-nav-link ${
                  location.pathname === "/admin/settings" ? "active" : ""
                }`}
              >
                Settings
              </a>
            )}
            <a
              href="/dispute"
              onClick={(e) => handleNavClick(e, "/dispute")}
              className={`mobile-nav-link ${
                location.pathname === "/dispute" ? "active" : ""
              }`}
            >
              Tomi (Legacy)
            </a>

            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="mobile-nav-link mobile-logout-btn"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
    </nav>
  );
};

function App() {
  const AppContent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigate = (path: string) => {
      if (location.pathname === path) return;
      navigate(path);
    };

    return (
      <div className="App">
        <Navigation onNavigate={handleNavigate} />
        <main className="app-main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/dispute"
              element={
                <ProtectedRoute>
                  <DisputeForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rain"
              element={
                <ProtectedRoute>
                  <RainWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute adminOnly>
                  <HistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute superAdminOnly>
                  <UsersAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute superAdminOnly>
                  <SettingsAdmin />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
        <footer className="app-footer">
          <div className="footer-content">
            <span className="footer-text">© LEX {new Date().getFullYear()}</span>
          </div>
        </footer>
      </div>
    );
  };

  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
